import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import UserHeader from "../../components/UserHeader";
import Footer from "../../components/Footer";
import userStore from "../../zustand/userStore";
import { BASE_URL } from "../../configs/WebServiceUrl";
import { InitiatePaymentThunk } from "../../features/pricing/redux/thunks";
import { getStateNameFromGst, getStateCodeFromGst } from "../../utils/gstStateCodes";
import "./pricing.css";

const INR = (amount) => `₹${Number(amount || 0).toLocaleString("en-IN")}`;

// ── GST Popup (new) — defined OUTSIDE CoachingPricing to prevent remount on re-render ──
const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

function GSTPopup({
  open,
  gstChoice,
  gstNumber,
  gstError,
  onChoiceChange,
  onNumberChange,
  onSubmit,
  onCancel,
}) {
  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
          padding: "32px 28px 28px",
          width: "100%",
          maxWidth: "440px",
          fontFamily: "inherit",
        }}
      >
        <h3 style={{ margin: "0 0 6px", fontSize: "1.2rem", fontWeight: 700, color: "#1a1a2e" }}>
          GST Details
        </h3>
        <p style={{ margin: "0 0 20px", color: "#555", fontSize: "0.93rem" }}>
          Do you want a GST bill?
        </p>

        {/* Radio buttons */}
        <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
          {["yes", "no"].map((val) => (
            <label
              key={val}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
                fontSize: "0.97rem",
                fontWeight: gstChoice === val ? 600 : 400,
                color: gstChoice === val ? "#4f46e5" : "#333",
              }}
            >
              <input
                type="radio"
                name="gst_choice"
                value={val}
                checked={gstChoice === val}
                onChange={() => onChoiceChange(val)}
                style={{ accentColor: "#4f46e5", width: "16px", height: "16px" }}
              />
              {val === "yes" ? "Yes, I have GST" : "No, skip GST"}
            </label>
          ))}
        </div>

        {/* GST number input (visible only when "yes") */}
        {gstChoice === "yes" && (
          <div style={{ marginBottom: "18px" }}>
            <label
              htmlFor="gst_number_input"
              style={{
                display: "block",
                marginBottom: "6px",
                fontWeight: 600,
                fontSize: "0.9rem",
                color: "#333",
              }}
            >
              Enter your GST Number
            </label>
            <input
              id="gst_number_input"
              type="text"
              maxLength={15}
              placeholder="e.g. 29ABCDE1234F1Z5"
              value={gstNumber}
              onChange={(e) => onNumberChange(e.target.value.toUpperCase())}
              autoFocus
              style={{
                width: "100%",
                padding: "10px 12px",
                border: gstError ? "1.5px solid #ef4444" : "1.5px solid #d1d5db",
                borderRadius: "7px",
                fontSize: "0.97rem",
                outline: "none",
                boxSizing: "border-box",
                letterSpacing: "0.05em",
                transition: "border 0.2s",
              }}
              onFocus={(e) => { e.target.style.borderColor = "#4f46e5"; }}
              onBlur={(e) => { e.target.style.borderColor = gstError ? "#ef4444" : "#d1d5db"; }}
            />
            {gstError && (
              <p style={{ color: "#ef4444", fontSize: "0.82rem", marginTop: "5px", marginBottom: 0 }}>
                {gstError}
              </p>
            )}
            {!gstError && gstNumber.trim().length >= 2 && (
              <p
                style={{
                  color: getStateNameFromGst(gstNumber) ? "#16a34a" : "#dc2626",
                  fontSize: "0.82rem",
                  marginTop: "5px",
                  marginBottom: 0,
                  fontWeight: 500,
                }}
              >
                {getStateNameFromGst(gstNumber)
                  ? `State: ${getStateNameFromGst(gstNumber)} (Code: ${getStateCodeFromGst(gstNumber)})`
                  : `Unknown state code: ${getStateCodeFromGst(gstNumber)}`}
              </p>
            )}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", marginTop: "8px" }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: "9px 20px",
              borderRadius: "7px",
              border: "1.5px solid #d1d5db",
              background: "#fff",
              color: "#555",
              fontSize: "0.93rem",
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            style={{
              padding: "9px 24px",
              borderRadius: "7px",
              border: "none",
              background: "#4f46e5",
              color: "#fff",
              fontSize: "0.95rem",
              cursor: "pointer",
              fontWeight: 600,
              letterSpacing: "0.02em",
            }}
          >
            {gstChoice === "yes" ? "Submit & Pay" : "Proceed to Pay"}
          </button>
        </div>
      </div>
    </div>
  );
}
// ───────────────────────────────────────────────────────────────────────────────

export default function CoachingPricing({ embedded = false }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userData = userStore((state) => state.user);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── GST Popup state (new) ──────────────────────────────────────────────────
  const [gstPopupOpen, setGstPopupOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [gstChoice, setGstChoice] = useState("no");
  const [gstNumber, setGstNumber] = useState("");
  const [gstError, setGstError] = useState("");
  // ─────────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const fetchCoachingPlans = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${BASE_URL}/api/subscriptionmodel/coaching`);
        const data = Array.isArray(response.data) ? response.data : [];
        setPlans(data);
        setError(null);
      } catch (err) {
        console.error("Error fetching coaching subscription models:", err);
        setError("Failed to load coaching plans.");
      } finally {
        setLoading(false);
      }
    };

    fetchCoachingPlans();
  }, []);

  // ── GST Popup handlers (new) ──────────────────────────────────────────────
  const handleSubscribeClick = (plan) => {
    setSelectedPlan(plan);
    setGstChoice("no");
    setGstNumber("");
    setGstError("");
    setGstPopupOpen(true);
  };

  const handleGstChoiceChange = (val) => {
    setGstChoice(val);
    setGstError("");
  };

  const handleGstNumberChange = (val) => {
    setGstNumber(val);
    setGstError("");
  };

  const handleGstSubmit = () => {
    let formattedGst = "";
    let stateName = "";
    let stateCode = "";

    if (gstChoice === "yes") {
      formattedGst = gstNumber.trim().toUpperCase();
      if (!formattedGst) {
        setGstError("GST number is required.");
        return;
      }
      if (!GST_REGEX.test(formattedGst)) {
        setGstError("Enter a valid 15-character Indian GST number.");
        return;
      }
      stateName = getStateNameFromGst(formattedGst);
      stateCode = getStateCodeFromGst(formattedGst);
      if (!stateName) {
        setGstError(`Invalid state code (${stateCode}) in GST number.`);
        return;
      }
    }

    setGstPopupOpen(false);
    handleCoachingPayment(selectedPlan, {
      is_gst: gstChoice === "yes",
      gst_number: formattedGst,
      state: stateName,
      state_name: stateName,
      state_code: stateCode,
    });
  };

  const handleGstCancel = () => {
    setGstPopupOpen(false);
  };
  // ─────────────────────────────────────────────────────────────────────────────

  const handleCoachingPayment = (plan, gstData = {}) => {
    if (!userData) {
      alert("Please login to continue.");
      return;
    }

    const gstPayload = {
      is_gst: Boolean(gstData?.is_gst),
      gst_number: gstData?.gst_number || "",
      state_name: gstData?.state_name || "",
      state_code: gstData?.state_code || "",
    };

    const body = {
      txnRefNo: Date.now(),
      amount: plan?.price || 0,
      txnType: "Pay",
      orderInfo: String(plan?.model_id || ""),
      email: userData?.email || "",
      firstName: userData?.admin_name || userData?.name || "",
      lastName: "",
      street: "Delhi",
      city: "",
      state: "",
      zip: 506002,
      phone: userData?.contact_number || 0,
      udf21: userData?.admin_id || userData?.id,
      udf22: userData?.id,
      udf23: "",
      udf24: plan?.model_type || "coaching",
      udf25: plan?.total_users || 1,
      udf26: "",
      udf27: String(plan?.model_id || ""),
      udf28: JSON.stringify(gstPayload),
      udf29: JSON.stringify([
        {
          plan_name: plan?.model_name || plan?.planTitle || "Coaching Plan",
          model_id: plan?.model_id || null,
          actual_amount: Number(plan?.price) || 0,
          discount_amount: 0,
          discount_percentage: 0,
          final_amount: Number(plan?.price) || 0,
          total_users: plan?.total_users || 1,
          coupon_code: "",
        },
      ]),
      // GST fields from popup (new)
      // ...gstData,
    };

    console.log("Coaching Plan Payment Body:", body);
    dispatch(InitiatePaymentThunk(body));
  };

  return (
    <div className="dw-app">
      {/* GST Popup (new) */}
      <GSTPopup
        open={gstPopupOpen}
        gstChoice={gstChoice}
        gstNumber={gstNumber}
        gstError={gstError}
        onChoiceChange={handleGstChoiceChange}
        onNumberChange={handleGstNumberChange}
        onSubmit={handleGstSubmit}
        onCancel={handleGstCancel}
      />

      {!embedded && <UserHeader />}

      {!embedded && (
        <section className="pl-hero">
          <h1 className="pl-hero-title">Coaching &amp; Tuition Centre Plans</h1>
          <p className="pl-hero-sub">
            Choose a plan based on the size of your teaching centre.
          </p>
        </section>
      )}

      <main className={`pl-page${embedded ? "" : " has-hero"}`}>
        {loading ? (
          <div className="text-center my-5 fs-5">Loading Coaching Plans...</div>
        ) : error ? (
          <div className="text-center my-5 text-danger fs-5">{error}</div>
        ) : plans.length === 0 ? (
          <div className="text-center my-5 fs-5">No coaching plans available.</div>
        ) : (
          <div className="pl-grid">
            {plans.map((tier, index) => {
              const planTitle = `Plan ${index + 1}`;
              let addData = {};
              if (tier.additional_data) {
                try {
                  addData =
                    typeof tier.additional_data === "string"
                      ? JSON.parse(tier.additional_data)
                      : tier.additional_data;
                } catch (e) {
                  addData = {};
                }
              }
              const perUserPrice =
                addData.price_per_teacher || addData.price_per_user || null;

              return (
                <section key={tier.model_id || tier.id || index} className="pl-card">
                  <p className="pl-card-name">{planTitle}</p>

                  {tier.description && (
                    <p className="pl-capacity" style={{ whiteSpace: "pre-line" }}>
                      For {tier.description}
                    </p>
                  )}

                  {tier.total_users && (
                    <p className="pl-storage">Users: {tier.total_users}</p>
                  )}

                  <p className="pl-storage">Valid for 1 Year</p>

                  <div className="pl-price">{INR(tier.price)}</div>

                  {perUserPrice && (
                    <p className="pl-per-user">{INR(perUserPrice)} per user</p>
                  )}

                  <div className="pl-actions">
                    <button
                      type="button"
                      className="dw-btn dw-btn-primary pl-btn-block"
                      onClick={() => handleSubscribeClick({ ...tier, planTitle, perUserPrice })}
                    >
                      Subscribe
                    </button>
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </main>

      {!embedded && <Footer userData={userData} />}
    </div>
  );
}
